// ✅ src/pages/Vehicle/VehicleMainTypeMaster.jsx  (FULL FILE - UPDATED WITH LOCALSTORAGE HEADERS)
// Backend mount:
//   app.use("/api/mainTypeMaster", vehicleMainTypeMaster);
//
// Endpoints assumed:
//   GET    https://nissi-india.com/api/mainTypeMaster            -> list (optional ?search=)
//   GET    https://nissi-india.com/api/mainTypeMaster/:id        -> get one (edit mode)
//   POST   https://nissi-india.com/api/mainTypeMaster            -> create
//   PUT    https://nissi-india.com/api/mainTypeMaster/:id        -> update
//
// Usage:
//   Create: /Vehicle/VehicleMainTypeMaster
//   Edit:   /Vehicle/VehicleMainTypeMaster?id=12

import HeaderBar from "../../components/HeaderBar";
import { useEffect, useMemo, useState, useContext } from "react";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft, FaSave, FaPlus, FaEdit, FaTimes } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/mainTypeMaster";

const toNum = (v) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : 0;
};

const readQS = (search) => {
  const sp = new URLSearchParams(search);
  const obj = {};
  for (const [k, v] of sp.entries()) obj[k] = v;
  return obj;
};

export default function VehicleMainTypeMaster() {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ✅ edit mode via querystring: ?id=12
  const qs = useMemo(() => readQS(location.search), [location.search]);
  const editId = useMemo(() => toNum(qs.id || qs.mainTypeId || 0), [qs]);

  const [vehicleMainType, setVehicleMainType] = useState("");
  const [saving, setSaving] = useState(false);

  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [search, setSearch] = useState("");

  const [loadingEdit, setLoadingEdit] = useState(false);

  const fetchList = async (searchText = "") => {
    setLoadingList(true);
    try {
      const r = await axios.get(API_BASE, {
        params: { search: String(searchText || "").trim() },
        headers: commonHeaders,
      });
      const data = r?.data?.data ?? [];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("List error:", e);
      toast.error("Failed to load vehicle main types!");
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchEdit = async (id) => {
    if (!id) return;
    setLoadingEdit(true);
    try {
      const r = await axios.get(`${API_BASE}/${id}`, {
        headers: commonHeaders,
      });
      const d = r?.data?.data;
      setVehicleMainType(String(d?.vehicleMainType ?? d?.fld_vehicletypename ?? "").trim());
    } catch (e) {
      console.error("Edit load error:", e);
      toast.error("Failed to load selected record!");
    } finally {
      setLoadingEdit(false);
    }
  };

  useEffect(() => {
    fetchList("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  useEffect(() => {
    if (editId) fetchEdit(editId);
    else setVehicleMainType("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, commonHeaders]);

  const clearForm = () => {
    setVehicleMainType("");
    navigate("/vehicle/VehicleMainTypeMaster", { replace: true });
  };

  const handleSave = async () => {
    const name = String(vehicleMainType || "").trim();
    if (!name) {
      toast.error("Please enter a vehicle main type!");
      return;
    }

    setSaving(true);
    try {
      if (editId) {
        await axios.put(
          `${API_BASE}/${editId}`,
          { vehicleMainType: name },
          { headers: commonHeaders }
        );
        toast.success("Vehicle Main Type updated successfully!");
      } else {
        await axios.post(
          API_BASE,
          { vehicleMainType: name },
          { headers: commonHeaders }
        );
        toast.success("Vehicle Main Type saved successfully!");
      }

      await fetchList(search);
      clearForm();
    } catch (error) {
      console.error("Save error:", error);
      const msg =
        error?.response?.data?.message ||
        (editId ? "Failed to update vehicle main type!" : "Failed to save vehicle main type!");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    const id = toNum(row?.id ?? row?.fld_vehicletypeid);
    if (!id) return;
    navigate(`/vehicle/VehicleMainTypeMaster?id=${id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const shellCard = `p-6 mt-6 rounded-2xl shadow-xl border ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
      : `bg-white ${currentSkin.glass} text-black`
  }`;

  const innerCard = `rounded-xl p-6 border ${
    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
  }`;

  const inputClass = `w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">VEHICLE MAIN TYPE MASTER</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className={shellCard}>
        {/* Top Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div
              className={`p-3 rounded-xl ${
                isDark ? "bg-white/10" : "bg-gray-100"
              }`}
            >
              {editId ? <FaEdit /> : <FaPlus />}
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {editId ? "Edit Vehicle Main Type" : "Add Vehicle Main Type"}
              </h1>
              <p className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm`}>
                {editId ? `Editing ID: ${editId}` : "Create and manage main vehicle types"}
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? "border-gray-600 text-white hover:bg-gray-800"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <FaArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        {/* Form */}
        <div className={innerCard}>
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <label
                htmlFor="vehicleMainType"
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Vehicle Main Type
              </label>

              <input
                id="vehicleMainType"
                type="text"
                value={vehicleMainType}
                onChange={(e) => setVehicleMainType(e.target.value)}
                placeholder={loadingEdit ? "Loading..." : "Enter vehicle main type..."}
                disabled={loadingEdit}
                className={inputClass}
              />

              <div className={`text-xs mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Duplicate names are not allowed (same as ASPX).
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              {editId ? (
                <button
                  onClick={clearForm}
                  className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                >
                  <FaTimes />
                  Cancel Edit
                </button>
              ) : (
                <button
                  onClick={() => setVehicleMainType("")}
                  className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Clear
                </button>
              )}

              <button
                onClick={handleSave}
                disabled={saving || !vehicleMainType.trim() || loadingEdit}
                className={`flex items-center justify-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  saving || !vehicleMainType.trim() || loadingEdit
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    {editId ? "Update" : "Save"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
}